import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Plus, ArrowRight, Tag, RefreshCw, FileText, UserCircle, DollarSign } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { getMyVehicles, CustomerVehicle, getMyProfile } from "@/Calls/callCustomer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function CustomerDashboard() {
    const { user } = useAuth();
    const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [profileIncomplete, setProfileIncomplete] = useState(false);

    useEffect(() => {
        Promise.all([
            getMyVehicles(),
            getMyProfile(),
        ]).then(([v, p]) => {
            setVehicles(v);
            const incomplete = !p.customer_fname || !p.customer_lname || !p.customer_home_addr1;
            setProfileIncomplete(incomplete);
        }).catch(console.error).finally(() => setLoading(false));
    }, []);

    const fsbo = vehicles.filter((v) => v.trade_type === "FSBO");
    const trades = vehicles.filter((v) => v.trade_type === "TRADE_IN");
    const active = vehicles.filter((v) => v.trade_status === "ACTIVE");

    return (
        <div className="space-y-8 max-w-5xl mx-auto">
            {/* Welcome */}
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">
                    Welcome back{user ? "" : ""}
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Manage your vehicles, trade-ins, and listings from one place.
                </p>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="p-5 flex items-center gap-4">
                    <div className="rounded-full bg-indigo-100 p-3">
                        <Car className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-semibold">{loading ? "—" : vehicles.length}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Vehicles</p>
                    </div>
                </Card>

                <Card className="p-5 flex items-center gap-4">
                    <div className="rounded-full bg-emerald-100 p-3">
                        <Tag className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-semibold">{loading ? "—" : fsbo.length}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">For Sale (FSBO)</p>
                    </div>
                </Card>

                <Card className="p-5 flex items-center gap-4">
                    <div className="rounded-full bg-amber-100 p-3">
                        <RefreshCw className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-semibold">{loading ? "—" : trades.length}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Trade-Ins</p>
                    </div>
                </Card>
            </div>

            {/* Profile completion CTA */}
            {profileIncomplete && (
                <Link to="/customer/profile">
                    <Card className="p-4 flex items-center gap-3 border-amber-300 bg-amber-50 dark:bg-amber-950/20 hover:border-amber-400 transition-colors cursor-pointer">
                        <div className="rounded-full bg-amber-100 p-2 shrink-0">
                            <UserCircle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                            <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">Complete your profile</p>
                            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">Add your address and employment info to speed up deal processing.</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-amber-600 shrink-0" />
                    </Card>
                </Link>
            )}

            {/* Quick actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Link to="/customer/vehicles/upload">
                    <Card className="p-6 flex items-center justify-between hover:border-indigo-400 transition-colors cursor-pointer group">
                        <div>
                            <p className="font-medium">List a Vehicle</p>
                            <p className="text-sm text-muted-foreground mt-1">Trade-in or sell privately</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Plus className="h-5 w-5 text-indigo-500" />
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Card>
                </Link>

                <Link to="/customer/vehicles">
                    <Card className="p-6 flex items-center justify-between hover:border-indigo-400 transition-colors cursor-pointer group">
                        <div>
                            <p className="font-medium">My Vehicles</p>
                            <p className="text-sm text-muted-foreground mt-1">View and manage your listings</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </Card>
                </Link>

                <Link to="/customer/deals">
                    <Card className="p-6 flex items-center justify-between hover:border-indigo-400 transition-colors cursor-pointer group">
                        <div>
                            <p className="font-medium">My Deals</p>
                            <p className="text-sm text-muted-foreground mt-1">Track deal status in real time</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <DollarSign className="h-5 w-5 text-emerald-500" />
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Card>
                </Link>

                <Link to="/customer/documents">
                    <Card className="p-6 flex items-center justify-between hover:border-indigo-400 transition-colors cursor-pointer group">
                        <div>
                            <p className="font-medium">My Documents</p>
                            <p className="text-sm text-muted-foreground mt-1">Download buyer's orders & credit apps</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-violet-500" />
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Card>
                </Link>
            </div>

            {/* Recent vehicles */}
            {!loading && active.length > 0 && (
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Active Listings</h2>
                        <Link to="/customer/vehicles" className="text-xs text-indigo-500 hover:underline">
                            View all
                        </Link>
                    </div>
                    <div className="space-y-3">
                        {active.slice(0, 3).map((v) => (
                            <Card key={v.trade_id} className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="rounded-lg bg-muted p-2">
                                        <Car className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm">
                                            {v.trade_year} {v.trade_make} {v.trade_model}
                                            {v.trade_trim ? ` ${v.trade_trim}` : ""}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {v.trade_type === "FSBO" ? "For Sale" : "Trade-In"} •{" "}
                                            {v.trade_miles?.toLocaleString()} mi
                                            {v.trade_price ? ` • $${Number(v.trade_price).toLocaleString()}` : ""}
                                        </p>
                                    </div>
                                </div>
                                <Link to={`/customer/vehicles/${v.trade_id}`}>
                                    <Button variant="ghost" size="sm">
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </Link>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {!loading && vehicles.length === 0 && (
                <Card className="p-10 text-center border-dashed">
                    <Car className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No vehicles yet</p>
                    <p className="text-sm text-muted-foreground mt-1 mb-5">
                        List your first vehicle for trade-in or private sale.
                    </p>
                    <Link to="/customer/vehicles/upload">
                        <Button className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
                            <Plus className="h-4 w-4 mr-2" />
                            List a Vehicle
                        </Button>
                    </Link>
                </Card>
            )}
        </div>
    );
}
